import {ChangeEvent, FC, useEffect} from 'react';
import {Controller, useForm} from 'react-hook-form';
import Button from '@atomaro/ui-kit/components/Button/Button/Button';
import Input from '@atomaro/ui-kit/components/Input/Input';
import Modal from '@atomaro/ui-kit/components/Modal/Modal';
import {CloseIcon} from 'shared/ui/icons';
import {toRem} from 'shared/helpers';
import {
  CreateApiaryModalBody,
  CreateApiaryModalFooter,
  CreateApiaryModalHead,
  CreateApiaryModalTitle,
  CreateApiaryModalWrapper,
} from 'features/apiary/CreateApiary/styles.ts';
import {useDevicesStore} from 'entities/devices';
import {RenameDeviceModalState} from 'entities/devices/model/types.ts';
import {useApiariesStore} from 'entities/apiaries';

type RenameModalProps = {
  modalState: RenameDeviceModalState;
  isMobile?: boolean;
  onClose: () => void;
};

type FormValues = {
  name: string;
};

export const RenameDeviceModal: FC<RenameModalProps> = ({
  modalState: {isOpen, name: defaultName, id},
  isMobile,
  onClose,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: {errors},
  } = useForm<FormValues>({
    defaultValues: {
      name: '',
    },
  });
  const {updateDevice, getDevicesFromServer} = useDevicesStore();
  const currentApiaryId = useApiariesStore((s) => s.currentApiaryId);

  const name = watch('name');

  useEffect(() => {
    if (isOpen) {
      reset({name: defaultName ?? ''});
    }
  }, [isOpen, defaultName, reset]);

  const onSubmit = async (data: FormValues) => {
    await updateDevice(id ?? '', data);
    await getDevicesFromServer({apiaryId: currentApiaryId!});
    onClose();
    reset({name: ''});
  };

  const onReset = () => {
    onClose();
    reset({name: defaultName ?? ''});
  };

  return (
    <Modal
      isOpen={isOpen}
      centered
      style={{padding: 0, maxHeight: '100%'}}
      onClickOverlay={onClose}
    >
      <CreateApiaryModalWrapper onSubmit={handleSubmit(onSubmit)}>
        <CreateApiaryModalHead>
          <CreateApiaryModalTitle>Сменить название</CreateApiaryModalTitle>
          <Button
            iconPrefix={<CloseIcon />}
            variant="ghost"
            colorScheme="neutral"
            style={{width: toRem(24)}}
            size={isMobile ? 's' : 'm'}
            onClick={onClose}
          />
        </CreateApiaryModalHead>

        <CreateApiaryModalBody>
          <Controller
            control={control}
            name="name"
            rules={{required: 'Введите название'}}
            render={({field}) => (
              <Input
                size="l"
                label="Название"
                name={field.name}
                value={field.value}
                onChange={(event: ChangeEvent<HTMLInputElement>) => field.onChange(event)}
                error={errors.name ? errors.name.message : undefined}
                defaultValue={defaultName ?? ''}
              />
            )}
          />
        </CreateApiaryModalBody>

        <CreateApiaryModalFooter>
          <Button label="Сохранить" type="submit" size="l" disabled={!name?.trim()} />
        </CreateApiaryModalFooter>
      </CreateApiaryModalWrapper>
    </Modal>
  );
};
